import { saleStat } from '../../../../data/product';
import type { ProductOrderItem } from '../data/product-order-item';

export type TierAllocation = {
  orderItem: ProductOrderItem;
  cases: number;
};

/**
 * Distributes `targetCases` across `items` to minimize projected days-on-hand
 * (DOH) at each step, using a 180-day sales velocity. Items with zero velocity
 * or zero case_qty are returned in `skipped` (not allocated to). Callers
 * decide whether to clear skipped items or leave them untouched.
 */
export function allocateCasesByDOH(
  items: ProductOrderItem[],
  targetCases: number
): { allocated: TierAllocation[]; skipped: ProductOrderItem[] } {
  type Working = {
    orderItem: ProductOrderItem;
    dailySalesRate: number;
    caseQty: number;
    inventoryQty: number;
    cases: number;
  };

  const working: Working[] = [];
  const skipped: ProductOrderItem[] = [];

  for (const item of items) {
    const dailySalesRate = saleStat(item.product, 180) / 180;
    if (dailySalesRate > 0 && item.product.case_qty > 0) {
      working.push({
        orderItem: item,
        dailySalesRate,
        caseQty: item.product.case_qty,
        inventoryQty: item.product.inventory_qty,
        cases: 0,
      });
    } else {
      skipped.push(item);
    }
  }

  const cases = Math.max(0, Math.floor(targetCases));
  for (let i = 0; i < cases && working.length > 0; i++) {
    let bestIdx = -1;
    let bestDOH = Number.POSITIVE_INFINITY;
    working.forEach((w, j) => {
      const projectedDOH = (w.inventoryQty + (w.cases + 1) * w.caseQty) / w.dailySalesRate;
      if (projectedDOH < bestDOH) {
        bestDOH = projectedDOH;
        bestIdx = j;
      }
    });
    if (bestIdx >= 0) {
      working[bestIdx].cases += 1;
    }
  }

  return {
    allocated: working.map((w) => ({ orderItem: w.orderItem, cases: w.cases })),
    skipped,
  };
}
